// The copy rulebook for the notification Registry: where a notification's words appear, the fields
// each place has and their character limits, which places a notification may leave empty, and the
// variables copy may carry. The API validates with it and the admin renders with it, so a counter
// can never read green while the server says over, and a variable warning can never lag the words
// on screen.

const _ = require('lodash');

// Push has a title and a body; the rest are one string. `center_row_several` is the sentence an
// aggregated Notification Center row uses once more than one event lands in a day; `center_cta` is
// the verb on a Needs action button, and only that row has one.
const COPY_SURFACES = Object.freeze([
  'push_a',
  'push_b',
  'center_row',
  'center_row_several',
  'center_cta',
  'email_subject',
  'email_preview',
  'email_body'
]);

// Character limits by field. Push title and body apply to both push surfaces; the email body's
// paragraphs have none, only the words on its button do.
const COPY_LIMITS = Object.freeze({
  push_title: 40,
  push_body: 150,
  center_row: 80,
  center_cta: 12,
  email_subject: 50,
  email_preview: 90,
  email_button: 24
});

const SURFACE_FIELDS = Object.freeze({
  push_a: Object.freeze({ title: COPY_LIMITS.push_title, body: COPY_LIMITS.push_body }),
  push_b: Object.freeze({ title: COPY_LIMITS.push_title, body: COPY_LIMITS.push_body }),
  center_row: Object.freeze({ text: COPY_LIMITS.center_row }),
  center_row_several: Object.freeze({ text: COPY_LIMITS.center_row }),
  center_cta: Object.freeze({ verb: COPY_LIMITS.center_cta }),
  email_subject: Object.freeze({ text: COPY_LIMITS.email_subject }),
  email_preview: Object.freeze({ text: COPY_LIMITS.email_preview }),
  email_body: Object.freeze({ text: null, button: COPY_LIMITS.email_button, buttonUrl: null })
});
const emailButtonUrlInputName = value => /^\{\{\s*([A-Za-z_]\w*)\s*\}\}$/.exec(String(value || '').trim())?.[1] || null;
const isHttpsButtonUrl = value => /^https:\/\/[^\s"<>{}]+$/i.test(String(value || '').trim());

// The email body's paragraphs: the one field that keeps its line breaks, where every other field is one line.
const isParagraphField = (surface, field) => surface === 'email_body' && field === 'text';

// Surfaces a notification may leave empty without being short of copy: the A/B variant, the email
// preview line, and the Notification Center button, which reads View until someone chooses a verb.
const OPTIONAL_SURFACES = Object.freeze(['push_b', 'email_preview', 'center_cta']);

// Approved words the backend reads at send time: Push A/B, the email subject, and the Notification
// Center sentence and button. Email preview and email body are signed off here and go live in the
// template or the Klaviyo flow.
const SEND_TIME_SURFACES = Object.freeze(['push_a', 'push_b', 'email_subject', 'center_row', 'center_row_several', 'center_cta']);

// Version 0 is the one mutable working draft per surface; every Approve copies it into the next
// immutable numbered version.
const WORKING_DRAFT_VERSION = 0;

// A template in code is the send path whatever the plan's Sent from still says: the backend reads
// approved words by type and never looks at that label. Only a lane with no template is external.
const sendsFromBackend = ({ sentFrom, hasTemplate = false }) => hasTemplate || !sentFrom || sentFrom === 'backend';

// A Klaviyo flow's push and email are the flow, unless a template in code sends that channel from the
// backend: that send reads the approved words whatever the plan's Sent from says. A declared delivery
// is only a plan. marketingEventSendsPush is Klaviyo's send, not the backend's.
const KLAVIYO_EMAIL_COPY_NOTE = 'This is a Klaviyo flow. Please manage the email copy in Klaviyo.';
const KLAVIYO_PUSH_COPY_NOTE = 'This is a Klaviyo flow. Please manage the push copy in Klaviyo.';

const emailCopyLivesInKlaviyo = ({ sentFrom, hasTemplate = false, emailDelivery = null }) =>
  sentFrom === 'klaviyo_flow' && !(hasTemplate && emailDelivery === 'backend');

const pushCopyLivesInKlaviyo = ({ sentFrom, hasTemplate = false, pushDelivery = null }) =>
  sentFrom === 'klaviyo_flow' && !(hasTemplate && pushDelivery === 'backend');

// Our backend decides and writes the Center row, then fires the template's Klaviyo event; a flow on
// that event delivers the push and email. Read from the template, never stored as its own Sent from.
const backendDeliveredByKlaviyo = ({ sentFrom, pushDelivery = null }) => sentFrom === 'backend' && pushDelivery === 'klaviyo';

// A Klaviyo flow is in the send path, whether Klaviyo decides or our backend does and the flow delivers.
const usesKlaviyoFlow = ({ sentFrom, pushDelivery = null }) => sentFrom === 'klaviyo_flow' || backendDeliveredByKlaviyo({ sentFrom, pushDelivery });

// The push words, and the email words when that email is the flow's twin: approved here as Lifecycle's
// starting point, and Live in flow once they are in it. A backend-rendered email stays a backend send.
const isKlaviyoDeliveredSurface = ({ surface, sentFrom, pushDelivery = null, emailDelivery = null }) => {
  const name = String(surface || '');
  if (!backendDeliveredByKlaviyo({ sentFrom, pushDelivery })) return false;
  if (name.startsWith('push')) return true;
  return name.startsWith('email') && name !== 'email_body' && emailDelivery === 'klaviyo_twin';
};

const klaviyoCopyNote = surface => {
  const name = String(surface || '');
  if (name.startsWith('push')) return KLAVIYO_PUSH_COPY_NOTE;
  if (name.startsWith('email')) return KLAVIYO_EMAIL_COPY_NOTE;
  return null;
};

const isKlaviyoOwnedEmailSurface = ({ surface, sentFrom, hasTemplate, emailDelivery }) =>
  emailCopyLivesInKlaviyo({ sentFrom, hasTemplate, emailDelivery }) && String(surface || '').startsWith('email');

const isKlaviyoOwnedCopySurface = ({ surface, sentFrom, hasTemplate = false, emailDelivery = null, pushDelivery = null }) => {
  const name = String(surface || '');
  if (name === 'email_body' && backendDeliveredByKlaviyo({ sentFrom, pushDelivery }) && emailDelivery === 'klaviyo_twin') return true;
  if (name.startsWith('email')) return emailCopyLivesInKlaviyo({ sentFrom, hasTemplate, emailDelivery });
  if (name.startsWith('push')) return pushCopyLivesInKlaviyo({ sentFrom, hasTemplate, pushDelivery });
  return false;
};

const approvalReplacesLiveSend = ({ surface, sentFrom, hasTemplate = false, pushDelivery = null, emailDelivery = null }) =>
  SEND_TIME_SURFACES.includes(surface) &&
  sendsFromBackend({ sentFrom, hasTemplate }) &&
  !isKlaviyoDeliveredSurface({ surface, sentFrom, pushDelivery, emailDelivery });

// Where a notification is on its way out. Paused and Retired come from the Registry status. A Klaviyo
// flow, a OneSignal dashboard send, or a lane with no template reads its status too, because no launch
// row decides its sends. Everything else reads its launch mode, and is Shadow without one.
const launchStageFor = ({ status, sentFrom, hasTemplate, mode }) => {
  if (['paused', 'retired'].includes(status)) return status;
  if (!hasTemplate || ['klaviyo_flow', 'onesignal_dashboard'].includes(sentFrom)) return status;
  return mode || 'shadow';
};

// The only dotted paths copy may read from an object input. Anything else on those objects (tokens,
// keys, ids, emails) is not renderable, so a draft naming it fails the gate and the send falls back
// to the template's words. Extend here when copy needs a new field; the gate, the runtime read and
// the admin's insert rail read one list.
const COPY_VARIABLE_PATHS = Object.freeze({
  user: ['name', 'firstName', 'lastName', 'username'],
  brand: ['name'],
  messageBrand: ['name'],
  opportunity: ['title'],
  lookbook: ['title'],
  pin: ['title'],
  product: ['title'],
  collection: ['name'],
  consult: ['title'],
  userTier: ['label']
});

// Every send carries the recipient, so `{{ user.name }}` is always legal even though Inputs does not list it.
const ALWAYS_KNOWN_INPUTS = Object.freeze(['user']);

const ALWAYS_KNOWN_VARIABLE_EXAMPLES = Object.freeze({
  'user.name': 'Jane Creator',
  'user.firstName': 'Jane',
  'user.lastName': 'Creator',
  'user.username': 'janecreator'
});

// The running totals an aggregated row is re-rendered with once more than one event lands in a day.
// Only the several sentence ever sees them, so they are legal there and nowhere else.
const AGGREGATE_VARIABLE_EXAMPLES = Object.freeze({
  aggregateCount: '3',
  aggregateValue: '94',
  participantNames: 'Glossier and Sephora'
});

const AGGREGATE_VARIABLE_SURFACES = Object.freeze(['center_row_several']);

// Cases let one surface hold several sentences: an ordered list of conditions over the row's own
// case fields, each with its own sentence, and the surface's own `text` as the sentence that applies
// when none of them do. Order carries no precedence, so two conditions that can be true at once are
// refused rather than silently ranked.
const CASE_SURFACES = Object.freeze(['center_row', 'center_row_several']);

const CASE_OPERATORS = Object.freeze(['is', 'gt', 'lt']);

const CASE_OPERATOR_LABELS = Object.freeze({ is: 'is exactly', gt: 'is more than', lt: 'is less than' });

// A condition reads either a number or one of a declared set of words. The caller says which by
// listing a field's values in `caseEnums`; everything else is a number, and a field that holds words
// can only be compared for equality.
const ENUM_CASE_OPERATORS = Object.freeze(['is']);

// The running totals a several row is re-rendered with are the numbers a condition can read.
const CASE_FIELDS = Object.freeze(['aggregateCount', 'aggregateValue']);
const MAX_CASES = 12;

// The field a Live Thread branches on: the status the row has just been rewritten to.
const THREAD_CASE_FIELD = 'status';

const supportsCases = surface => CASE_SURFACES.includes(surface);

const casesOf = content => (Array.isArray(content?.cases) ? content.cases : []);

// The sentence a center surface holds for these values. A count or amount uses the case that
// matches, and the surface's own text when none do. A declared word — a status, or any other
// enum the row branches on — uses the one sentence written for that word. Nothing is left over,
// so a word the set does not name has no sentence. Two cases that both match have no order to
// pick from, so the caller keeps the template's words.
const sentenceForCopy = ({ content, values = {}, caseEnums = {} }) => {
  const cases = casesOf(content).filter(rule => String(rule?.text || '').trim());
  if (!cases.length) {
    const text = String(content?.text || '').trim();
    return text || null;
  }
  const matching = cases.filter(rule => caseApplies(rule, values, caseEnums));
  if (matching.length > 1) return null;
  if (matching.length === 1) return String(matching[0].text).trim();
  if (cases.some(rule => isWordCase(rule.path, caseEnums))) return null;
  const text = String(content?.text || '').trim();
  return text || null;
};

const isWordCase = (path, caseEnums = {}) => path === THREAD_CASE_FIELD || isEnumCaseField(path, caseEnums);

const caseApplies = (rule, values, caseEnums = {}) => {
  if (!rule?.path || rule.value === '' || rule.value == null || !String(rule.text || '').trim()) return false;
  if (isWordCase(rule.path, caseEnums)) return rule.op === 'is' && String(_.get(values, rule.path) ?? '') === String(rule.value);
  if (!isCompleteCase(rule)) return false;
  return matchesCase(rule, values, caseEnums);
};

const isEntityThread = rulebook => rulebook?.centerBehavior === 'aggregated' && rulebook?.centerWindow === 'entity';

// One sentence per declared word, in the order the declaration lists them. A word nobody has
// written yet is still a row, empty, so the writer can fill it. A sentence left behind by a word
// the declaration dropped stays visible as drift.
const lockedCases = (content, path, values = []) => {
  const written = casesOf(content).filter(rule => rule.path === path);
  const byValue = _.keyBy(written, rule => String(rule.value));
  return [
    ...values.map(value => byValue[value] || { path, op: 'is', value, text: '' }),
    ...written.filter(rule => !values.includes(String(rule.value)))
  ];
};

const statusCases = (content, statuses = []) => lockedCases(content, THREAD_CASE_FIELD, statuses);

// `{ path, values }` is the one field a row's sentence may branch on, compared only for equality.
const CASE_ENUM_PATH = /^[A-Za-z_]\w*(\.\w+)*$/;

const caseEnumFrom = raw => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const path = String(raw.path || '').trim();
  const values = (Array.isArray(raw.values) ? raw.values : []).map(value => String(value).trim()).filter(Boolean);
  if (!CASE_ENUM_PATH.test(path) || !values.length || values.length > MAX_CASES) return null;
  if (_.uniq(values).length !== values.length || values.some(value => value.length > 64)) return null;
  return { path, values };
};

const isEnumCaseField = (path, caseEnums = {}) => Array.isArray(caseEnums?.[path]);

const caseOperatorsFor = (path, caseEnums = {}) => (isEnumCaseField(path, caseEnums) ? ENUM_CASE_OPERATORS : CASE_OPERATORS);

// A condition naming a word the notification no longer declares can never match, so the sentence
// under it is drift the editor marks rather than a reason to refuse every other sentence beside it.
const isDriftedCase = (rule, caseEnums = {}) =>
  isEnumCaseField(rule?.path, caseEnums) &&
  rule?.value !== '' &&
  rule?.value != null &&
  !caseEnums[rule.path].includes(String(rule.value));

const isCompleteCase = (rule, caseEnums = {}) => {
  if (!rule?.path || !caseOperatorsFor(rule.path, caseEnums).includes(rule.op)) return false;
  if (rule.value === '' || rule.value == null) return false;
  return isEnumCaseField(rule.path, caseEnums) ? caseEnums[rule.path].includes(String(rule.value)) : Number.isFinite(Number(rule.value));
};

const matchesCase = (rule, values, caseEnums = {}) => {
  if (isEnumCaseField(rule.path, caseEnums)) return String(values?.[rule.path] ?? '') === String(rule.value);
  const left = Number(values?.[rule.path] ?? 0);
  const right = Number(rule.value);
  if (rule.op === 'is') return left === right;
  if (rule.op === 'gt') return left > right;
  if (rule.op === 'lt') return left < right;
  return false;
};

// The values worth testing the conditions against. A condition on a field that holds words can only
// name one of the words it was declared with, so those are tried whole. A condition on a number
// uses integer witnesses for counts and one witness from every stretch between named amounts for
// values that can carry cents.
const caseWitnessesFor = (cases, caseEnums = {}) => {
  const complete = cases.filter(rule => isCompleteCase(rule, caseEnums));
  const path = complete[0]?.path;
  if (!path) return [];
  if (isEnumCaseField(path, caseEnums)) return caseEnums[path].map(value => ({ [path]: value }));
  const named = _.sortBy(_.uniq(complete.map(rule => Number(rule.value))));
  if (path === 'aggregateCount') {
    const integers = _.flatMap(named, value => [Math.floor(value) - 1, Math.floor(value), Math.ceil(value), Math.ceil(value) + 1]);
    return _.sortBy(_.uniq([0, ...integers].filter(value => value >= 0))).map(value => ({ [path]: value }));
  }
  const bounds = _.sortBy(_.uniq([0, ...named, _.last(named) + 1].filter(value => value >= 0)));
  const midpoints = bounds.slice(1).map((value, index) => (bounds[index] + value) / 2);
  return _.uniq([...bounds, ...midpoints]).map(value => ({ [path]: value }));
};

// Pairs of conditions that can both be true. Reported as index pairs so the editor can mark the rows
// a writer has to separate before the set can be approved.
const conflictingCasePairs = (cases, caseEnums = {}) => {
  const casePath = cases.find(rule => rule?.path)?.path;
  if (cases.length > MAX_CASES && !isEnumCaseField(casePath, caseEnums)) return [];
  const witnesses = caseWitnessesFor(cases, caseEnums);
  const pairs = [];
  const seenPairs = new Set();
  witnesses.forEach(witness => {
    const hits = cases
      .map((rule, index) => (isCompleteCase(rule, caseEnums) && matchesCase(rule, witness, caseEnums) ? index : null))
      .filter(index => index != null);
    for (let first = 0; first < hits.length; first += 1) {
      for (let second = first + 1; second < hits.length; second += 1) {
        const key = `${hits[first]}:${hits[second]}`;
        if (!seenPairs.has(key)) {
          seenPairs.add(key);
          pairs.push([hits[first], hits[second]]);
        }
      }
    }
  });
  return pairs;
};

// Structural rules for a case set. Sentence length stays a recommendation here as it is everywhere
// else; an incomplete condition, an empty sentence or an overlap is refused.
const validateCaseSet = ({ cases = [], caseFields = CASE_FIELDS, caseEnums = {}, fallbackText = null }) => {
  const errors = [];
  const live = cases.filter(rule => !isDriftedCase(rule, caseEnums));
  if (!live.length) return { errors, conflicts: [] };
  // A set reading a field that holds words has a case per word, so nothing is left over for a
  // fallback sentence to cover. A set reading a number always leaves the values no case names.
  if (fallbackText != null && !isEnumCaseField(live.find(rule => rule?.path)?.path, caseEnums) && !String(fallbackText).trim())
    errors.push('The "In all other cases" sentence is empty, so the days no case matches have no words.');
  live.forEach((rule, index) => {
    const position = index + 1;
    const isEnum = isEnumCaseField(rule?.path, caseEnums);
    if (!rule?.path) errors.push(`Case ${position} has no condition field.`);
    else if (!caseFields.includes(rule.path)) errors.push(`Case ${position} reads ${rule.path}, which this notification does not provide.`);
    if (!caseOperatorsFor(rule?.path, caseEnums).includes(rule?.op)) errors.push(`Case ${position} has no comparison.`);
    if (rule?.value === '' || rule?.value == null)
      errors.push(isEnum ? `Case ${position} names no ${rule.path}.` : `Case ${position} has no number to compare against.`);
    else if (!isEnum && !Number.isFinite(Number(rule.value))) errors.push(`Case ${position} has no number to compare against.`);
    if (!String(rule?.text || '').trim()) errors.push(`Case ${position} has no sentence.`);
  });
  const casePath = live.find(rule => rule?.path)?.path;
  if (cases.length > MAX_CASES && !isEnumCaseField(casePath, caseEnums)) {
    errors.push(`Use at most ${MAX_CASES} cases.`);
    return { errors, conflicts: [] };
  }
  // Conditions on two different things can always both be true at once, so a set that mixes them has
  // no order-free reading and is refused here rather than reported as a conflict on every pair.
  const fieldsRead = _.uniq(live.filter(rule => rule?.path).map(rule => rule.path));
  if (fieldsRead.length > 1) errors.push(`These cases read ${fieldsRead.length} different things. Every case has to read the same one.`);
  const conflicts = fieldsRead.length > 1 ? [] : conflictingCasePairs(live, caseEnums);
  conflicts.forEach(([first, second]) =>
    errors.push(`Cases ${first + 1} and ${second + 1} can both be true, so which one shows is undecided. Narrow one of them.`)
  );
  return { errors, conflicts };
};

// "{{ amount }}", "{amount}" and "{{ brand.name }}" (which reads from the input `brand`): the variables
// copy may carry, shared with the runtime read.
const COPY_VARIABLE_PATTERN = /\{\{?\s*([A-Za-z_][\w.]*)\s*\}?\}/g;

const extractVariables = text => _.uniq(Array.from(String(text || '').matchAll(COPY_VARIABLE_PATTERN), match => match[1]));

const interpolateWithExamples = (text, inputs = []) =>
  String(text || '').replace(COPY_VARIABLE_PATTERN, (match, name) => {
    const input = inputs.find(candidate => candidate.name === name.split('.')[0]);
    if (input?.example != null && input.example !== '') return String(input.example);
    return ALWAYS_KNOWN_VARIABLE_EXAMPLES[name] || AGGREGATE_VARIABLE_EXAMPLES[name] || match;
  });

// A variable copy may use: a root that is an input (or the recipient), and, when it reaches into an
// object, only a path on the allowlist. `brand.name` passes; `brand.developerKey` never does, and
// nor does a bare `brand`, which is the whole object and would render as nothing. Secret inputs are
// kept out of `inputNames` by copyInputNamesFor, so they never reach here as known.
const isRenderableVariable = (name, inputNames, surface = null) => {
  if (AGGREGATE_VARIABLE_EXAMPLES[name]) return AGGREGATE_VARIABLE_SURFACES.includes(surface);
  const [root, ...path] = name.split('.');
  if (!inputNames.includes(root) && !ALWAYS_KNOWN_INPUTS.includes(root)) return false;
  if (!path.length) return !COPY_VARIABLE_PATHS[root];
  return path.length === 1 && (COPY_VARIABLE_PATHS[root] || []).includes(path[0]);
};

const shouldRefuseUnknownCopyInputs = ({ unknownInputs = [], hasTemplate = false }) => unknownInputs.length > 0 && hasTemplate;

// The variable a writer should have used. An object input is named by its one printable path; an
// object off the copy paths has no variable at all, so nothing in copy can carry its value.
const literalExampleVariable = input => {
  if (COPY_VARIABLE_PATHS[input.name]) return `${input.name}.${COPY_VARIABLE_PATHS[input.name][0]}`;
  return input.isObject ? null : input.name;
};

const usesCopyVariable = (text, name) => new RegExp(`\\{\\{?\\s*${_.escapeRegExp(name)}(?:\\.\\w+)?\\s*\\}?\\}`).test(String(text || ''));

// An example value pasted in as the words themselves. "$250 has been added" sends $250 to everyone;
// "{{ paymentAmount }} has been added" does not. A field that already uses the variable may still
// mention the example. Two characters or fewer match ordinary words, so those examples are skipped.
// The example must stand alone, so an example of 907 does not match $1,907 or order #4907.
const literalExamplesIn = (content, inputs = []) => {
  const texts = copyWordsOf(content);
  return inputs.filter(input => {
    const example = String(input.example ?? '').trim();
    if (example.length <= 2) return false;
    const standalone = new RegExp(`(?<!\\w)(?<!\\d[.,])${_.escapeRegExp(example)}(?!\\w)(?![.,]\\d)`);
    return texts.some(text => standalone.test(String(text)) && !usesCopyVariable(text, input.name));
  });
};

const literalExampleRefusal = input => {
  const example = String(input.example).trim();
  const variable = literalExampleVariable(input);
  if (!variable)
    return `The draft writes ${example}, the example for ${input.name}, which no variable carries, so every send would say ${example}. Leave it out.`;
  return `The draft writes ${example} where {{ ${variable} }} belongs, so every send would say ${example}.`;
};

// Structural errors are refused. Lengths are recommendations because clients truncate differently;
// callers show them without blocking save or approval.
const validateCopyContent = ({
  surface,
  content,
  inputs = [],
  inputNames = inputs.map(input => input.name),
  secretInputs = [],
  caseFields = CASE_FIELDS,
  caseEnums = {},
  requireButtonLink = false
}) => {
  const fields = SURFACE_FIELDS[surface] || {};
  const errors = [];
  const unknownInputs = [];
  const overLimitFields = [];
  const objectInputNames = inputs.filter(input => input.isObject).map(input => input.name);
  const checkWords = ({ field, label, limit, value }) => {
    const rendered = interpolateWithExamples(value, inputs);
    if (limit && rendered.length > limit) overLimitFields.push({ field, renderedLength: rendered.length, limit });
    extractVariables(value).forEach(name => {
      const [root, ...path] = name.split('.');
      if (secretInputs.includes(root)) return errors.push(`${label} uses {{ ${name} }}, which the template keeps secret; copy cannot carry it.`);
      if (!path.length && COPY_VARIABLE_PATHS[root] && (inputNames.includes(root) || ALWAYS_KNOWN_INPUTS.includes(root)))
        return errors.push(`${label} uses {{ ${root} }}, which is the whole ${root}; write {{ ${root}.${COPY_VARIABLE_PATHS[root][0]} }}.`);
      if (!path.length && objectInputNames.includes(root))
        return errors.push(`${label} uses {{ ${root} }}, which is a whole object with no renderable path.`);
      if (!isRenderableVariable(name, inputNames, surface) && !unknownInputs.includes(name)) unknownInputs.push(name);
    });
    const unwrapped = value.replace(COPY_VARIABLE_PATTERN, '');
    // Only snake_case / camelCase object names: a lowercase word like "brand" is ordinary English.
    const bareObjectName = objectInputNames
      .filter(name => /_|[A-Z]/.test(name))
      .find(name => new RegExp(`\\b${_.escapeRegExp(name)}\\b`).test(unwrapped));
    if (bareObjectName) errors.push(`${label} uses ${bareObjectName} as plain text, but it is a template object, not printable copy.`);
    // Braces the pattern cannot read ("{{ PayPal | Stripe }}") are a note to the writer; the send would fall back to the template.
    const leftover = unwrapped.match(/\{\{[^}]*\}\}|\{\{|\}\}|\[[A-Za-z_]\w*(?:\.[A-Za-z_]\w*)*\]/);
    if (leftover) {
      const token = leftover[0].trim();
      errors.push(
        token.startsWith('[')
          ? `${label} has a square-bracket placeholder the system cannot read: ${token}. Write {{ ${token.slice(1, -1)} }} instead.`
          : `${label} has a placeholder the system cannot read: ${token}. Use one input name inside the braces.`
      );
    }
  };
  Object.entries(fields).forEach(([field, limit]) => checkWords({ field, label: field, limit, value: String(content?.[field] ?? '') }));
  if (surface === 'email_body') {
    const button = String(content?.button || '').trim();
    const buttonUrl = String(content?.buttonUrl || '').trim();
    if (requireButtonLink && button && !buttonUrl) errors.push('The button needs a link. Choose an input or enter an HTTPS URL.');
    if (requireButtonLink && buttonUrl && !button) errors.push('Add button text or clear its link.');
    const urlInputName = emailButtonUrlInputName(buttonUrl);
    if (buttonUrl && !isHttpsButtonUrl(buttonUrl) && !urlInputName)
      errors.push('The button link must be an HTTPS URL or one input, such as {{ offer_url }}.');
    const urlInput = inputs.find(input => input.name === urlInputName);
    if (urlInput?.example && !isHttpsButtonUrl(urlInput.example)) errors.push(`The button link input ${urlInputName} must have an HTTPS URL example.`);
  }
  const cases = supportsCases(surface) ? casesOf(content) : [];
  cases.forEach((rule, index) =>
    checkWords({ field: 'text', label: `Case ${index + 1}`, limit: fields.text, value: String(rule?.text ?? '') })
  );
  if (cases.length) errors.push(...validateCaseSet({ cases, caseFields, caseEnums, fallbackText: content?.text ?? '' }).errors);
  return { errors, unknownInputs, overLimitFields, literalExamples: literalExamplesIn(content, inputs) };
};

// A surface's content reduced to what it can hold: its own fields, and the case list where the
// surface supports one. Enum cases are exhaustive, so they have no fallback text.
const pickCopyContent = ({ surface, content, caseFields = [], caseEnums = {}, allowCases = supportsCases(surface) }) => {
  const fields = Object.keys(SURFACE_FIELDS[surface] || {});
  const picked = _.pick(content || {}, usesEnumCaseFields(caseFields, caseEnums) ? fields.filter(field => field !== 'text') : fields);
  if (!allowCases) return picked;
  const cases = casesOf(content).map(rule => ({
    path: rule?.path || null,
    op: rule?.op || null,
    value: caseValueOf(rule, caseEnums),
    text: String(rule?.text ?? '')
  }));
  return cases.length ? { ...picked, cases } : picked;
};

const usesEnumCaseFields = (caseFields = [], caseEnums = {}) => caseFields.some(field => Array.isArray(caseEnums[field]));

// Keys the current shape hides stay on the stored row so a window or behavior change can restore them.
const mergeHiddenCopy = ({ stored, visible, caseFields = [], caseEnums = {} }) => {
  const merged = { ...(visible || {}) };
  if (!caseFields.length && stored?.cases) merged.cases = stored.cases;
  if (usesEnumCaseFields(caseFields, caseEnums) && stored?.text != null && visible?.text === undefined) merged.text = stored.text;
  return merged;
};

// A condition's value is stored as what it compares: a number, or one of the declared words.
const caseValueOf = (rule, caseEnums) => {
  if (rule?.value === '' || rule?.value == null) return null;
  return isEnumCaseField(rule.path, caseEnums) ? String(rule.value) : Number(rule.value);
};

// Which surfaces a notification has words for, from its channels and Notification Center behavior.
// The button belongs to a row that needs action.
const applicableSurfaces = ({ channels = [], centerBehavior = null, centerWindow = null, requiresAction = false }) =>
  COPY_SURFACES.filter(surface => {
    if (surface.startsWith('push')) return channels.includes('push');
    if (surface.startsWith('email')) return channels.includes('email');
    if (surface === 'center_cta') return !!requiresAction && !!centerBehavior && centerBehavior !== 'none';
    if (surface === 'center_row_several') return centerBehavior === 'aggregated' && !isEntityThread({ centerBehavior, centerWindow });
    return !!centerBehavior && centerBehavior !== 'none';
  });

// Every sentence a surface's content holds, a case's included, as plain strings.
const copyWordsOf = content =>
  Object.entries(content || {}).flatMap(([field, value]) =>
    field === 'cases' ? casesOf(content).map(rule => String(rule?.text ?? '')) : [String(value ?? '')]
  );

const hasCopyWords = content => copyWordsOf(content).some(words => words.trim());

// The surfaces Draft with AI writes. The Notification Center button verb is chosen from a list, so it
// is not drafted; the email body is, until a template holds it.
const DRAFTABLE_SURFACES = Object.freeze(['push_a', 'push_b', 'center_row', 'center_row_several', 'email_subject', 'email_preview', 'email_body']);

// Draft with AI needs the purpose and the trigger; nothing else on the plan is required for a first draft.
const canDraftFromPlan = plan => !!String(plan?.purpose || '').trim() && !!String(plan?.triggerDescription || '').trim();

module.exports = {
  COPY_SURFACES,
  COPY_LIMITS,
  SURFACE_FIELDS,
  emailButtonUrlInputName,
  isHttpsButtonUrl,
  isParagraphField,
  OPTIONAL_SURFACES,
  SEND_TIME_SURFACES,
  WORKING_DRAFT_VERSION,
  sendsFromBackend,
  KLAVIYO_EMAIL_COPY_NOTE,
  KLAVIYO_PUSH_COPY_NOTE,
  emailCopyLivesInKlaviyo,
  pushCopyLivesInKlaviyo,
  backendDeliveredByKlaviyo,
  usesKlaviyoFlow,
  isKlaviyoDeliveredSurface,
  klaviyoCopyNote,
  isKlaviyoOwnedEmailSurface,
  isKlaviyoOwnedCopySurface,
  approvalReplacesLiveSend,
  launchStageFor,
  DRAFTABLE_SURFACES,
  COPY_VARIABLE_PATHS,
  ALWAYS_KNOWN_INPUTS,
  ALWAYS_KNOWN_VARIABLE_EXAMPLES,
  AGGREGATE_VARIABLE_EXAMPLES,
  AGGREGATE_VARIABLE_SURFACES,
  CASE_OPERATORS,
  CASE_OPERATOR_LABELS,
  CASE_FIELDS,
  MAX_CASES,
  THREAD_CASE_FIELD,
  supportsCases,
  casesOf,
  sentenceForCopy,
  isEntityThread,
  lockedCases,
  statusCases,
  caseEnumFrom,
  isEnumCaseField,
  conflictingCasePairs,
  validateCaseSet,
  COPY_VARIABLE_PATTERN,
  extractVariables,
  interpolateWithExamples,
  isRenderableVariable,
  shouldRefuseUnknownCopyInputs,
  literalExampleVariable,
  literalExampleRefusal,
  validateCopyContent,
  pickCopyContent,
  mergeHiddenCopy,
  usesEnumCaseFields,
  applicableSurfaces,
  copyWordsOf,
  hasCopyWords,
  canDraftFromPlan
};
